import { hasTeamView, isHrTeam, useCurrentUserRole } from '@components/Common/Utils/roles';
import Panel from '@components/Common/Panel';
import EmptyState from '@components/Common/EmptyState';
import { useChartColors } from '@components/Common/Charts/useChartColors';
import { APPLICATION_STAGES, isHired, reachedClient, statusToStage } from '@constants/applicationStatus';
import { jobList, jobApplicantsList, premiumEmployers } from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import CountUp from '@components/Common/CountUp';
import { BarChart } from '@mui/x-charts/BarChart';
import StatTile from '@components/Common/StatTile';
import { Box, LinearProgress, Typography } from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';

// Bounded fetch — the managed book is small; this keeps the dashboard snappy.
const APP_SCAN = 500;

// Bars past this point are unreadable in a column this wide, and a workload
// view is about who is at the top and who is idle, not the full roster.
const TOP_N = 8;

interface Kpis {
  premiumEmployers: number;
  activeRecruiters: number;
  activeRoles: number;
  submissions: number;
  hires: number;
  fillRate: number; // 0..1
}

interface StageCount {
  stage: string;
  count: number;
}

interface Workload {
  name: string;
  jobs: number;
}

const ManagedHiringSection: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [stages, setStages] = useState<StageCount[]>([]);
  const [workload, setWorkload] = useState<Workload[]>([]);
  /*
   * Recruiter workload compares HRs against each other: a team view, so a Super Admin's and an HR
   * Lead's (owner, 2026-11-03). Both may open /hr, which is where the tiles link.
   */
  const superAdmin = hasTeamView(useCurrentUserRole());
  /*
   * Not chartColor(): that resolves the token through getComputedStyle at CALL time, and this
   * section renders once and sits there, so a theme switch would recolour the console except
   * this chart. The hook keys the memo on the mode, which is the whole fix.
   */
  const [barColor] = useChartColors(1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const managedFilter = ['{ key: "job.hiringMode", value: "MANAGED" }'];
        const [empRes, appRes, jobRes, usersRes] = await Promise.all([
          fetchGraphQLApiData(premiumEmployers(0, 1)).catch(() => null),
          fetchGraphQLApiData(jobApplicantsList(0, APP_SCAN, managedFilter)).catch(() => null),
          fetchGraphQLApiData(jobList(0, 200, ['{ key: "hiringMode", value: "MANAGED" }'])).catch(() => null),
          // Only the team view shows the recruiter roster.
          superAdmin
            ? axios.get('/api/users?page=0&size=200').then((r) => r.data).catch(() => null)
            : Promise.resolve(null),
        ]);

        const premiumCount = empRes?.data?.getAllEmployers?.totalElements ?? 0;

        const apps = appRes?.data?.getAllJobApplications?.content ?? [];
        // Submissions are candidates put to a client, not every record: a managed job's pipeline
        // now starts at Sourcing, so the raw total would count people nobody has spoken to yet.
        const submissions = apps.filter((a: any) => reachedClient(a)).length;
        const hires = apps.filter((a: any) => isHired(a?.status, a?.subStatus)).length;

        const jobs = jobRes?.data?.getAllJobs?.content ?? [];
        const activeRoles = jobs.filter((j: any) => (j?.jobStatus ?? '').toLowerCase() === 'open').length;

        // Stage breakdown across the scanned managed applications.
        const stageMap: Record<string, number> = {};
        APPLICATION_STAGES.forEach((s) => (stageMap[s.stage] = 0));
        apps.forEach((a: any) => {
          const st = statusToStage(a?.status);
          if (st) stageMap[st] = (stageMap[st] ?? 0) + 1;
        });
        const stageCounts: StageCount[] = APPLICATION_STAGES.map((s) => ({ stage: s.stage, count: stageMap[s.stage] ?? 0 }));

        /*
         * Recruiter workload — MANAGED jobs assigned to each HR.
         *
         * Grouped from `jobs`, which is already in hand and already selects assignedHrId. This
         * used to fan out one getHrActiveClientCount query PER recruiter to count clients, which
         * was both the slowest thing on the dashboard and the wrong question: what a workload
         * view is asked is how many roles someone is carrying, not how many logos.
         */
        const hrUsers = (usersRes?.content ?? []).filter((u: any) => isHrTeam(u.role) && u.isActive !== false);
        const jobsByHr = new Map<string, number>();
        jobs.forEach((j: any) => {
          const hrId = j?.assignedHrId;
          if (!hrId) return;
          jobsByHr.set(hrId, (jobsByHr.get(hrId) ?? 0) + 1);
        });
        // Recruiters on zero stay in the list: an idle recruiter is exactly what this view is for.
        const workloads: Workload[] = hrUsers.map((u: any) => ({
          name:
            u.personalDetails?.fullName ||
            `${u.personalDetails?.firstName ?? ''} ${u.personalDetails?.lastName ?? ''}`.trim() ||
            u.personalDetails?.email ||
            'HR',
          jobs: jobsByHr.get(u._id) ?? 0,
        }));
        workloads.sort((a, b) => b.jobs - a.jobs);

        if (cancelled) return;
        setKpis({
          premiumEmployers: premiumCount,
          activeRecruiters: hrUsers.length,
          activeRoles,
          submissions,
          hires,
          fillRate: activeRoles > 0 ? hires / activeRoles : 0,
        });
        setStages(stageCounts);
        setWorkload(workloads);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // superAdmin gates the roster fetch, so a profile that resolves after first render has to
    // re-run this — otherwise the chart is permanently empty for the people it is built for.
  }, [superAdmin]);

  /*
   * A tile links only where the destination lists exactly what the tile counts.
   *
   * "Managed Clients" now does: /hr is titled "HR: Managed Clients" and is driven by the same
   * premiumEmployers query this KPI counts, so the click lands on the identical record set. It
   * previously had no link because the Employers page cannot filter to managed accounts -- its
   * filter map exposes premiumRequestStatus, not isPremium -- and the reasoning stopped at
   * Employers without noticing /hr already was the page.
   *
   * Both links are Super-Admin only, because /hr is hideForHr in NavigationItems: offering an HR
   * a link to a page their role cannot open is worse than offering none.
   *
   * The rest stay unlinked for the original reason. Jobs cannot filter by hiringMode, and
   * Submissions/Hires have no filtered application view, so those would land on an unfiltered
   * list showing a different number than the tile -- which teaches people the click is not worth
   * making. Fill Rate is a ratio and has no record set at all.
   */
  const cards = kpis
    ? [
        {
          label: 'Premium Hiring Clients',
          value: kpis.premiumEmployers,
          ...(superAdmin
            ? { to: '/hr', linkLabel: 'Open the Premium Hiring client list' }
            : {}),
        },
        // Roster count — only resolved / relevant for Super Admins.
        ...(superAdmin
          ? [
              {
                label: 'Active Recruiters',
                value: kpis.activeRecruiters,
                to: '/hr',
                linkLabel: 'Open the recruiter roster',
              },
            ]
          : []),
        { label: 'Active Premium Hiring Roles', value: kpis.activeRoles },
        { label: 'Submissions', value: kpis.submissions },
        { label: 'Hires', value: kpis.hires },
        { label: 'Fill Rate', value: `${Math.round(kpis.fillRate * 100)}%` },
      ]
    : [];

  const maxStage = Math.max(1, ...stages.map((s) => s.count));
  const topWorkload = workload.slice(0, TOP_N);

  return (
    <Panel className="db-section">
      <Box className="db-section-header">
        <Typography className="db-section-title" variant="subtitle1">
          Premium Hiring
        </Typography>
        <Typography className="db-section-sub" variant="body2">
          Submissions, hires, fill rate, and jobs per recruiter
        </Typography>
      </Box>

      {/* KPI cards */}
      <Box className="cc-stat-grid cc-motion-stagger">
        {(loading ? Array.from({ length: 6 }) : cards).map((card: any, i) => (
          <StatTile
            key={card?.label ?? i}
            label={card?.label ?? ''}
            value={typeof card?.value === 'number' ? <CountUp value={card.value} /> : card?.value}
            to={loading ? undefined : card?.to}
            linkLabel={card?.linkLabel}
            loading={loading}
          />
        ))}
      </Box>

      {/* Stage breakdown + recruiter workload */}
      {!loading && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: superAdmin ? '3fr 2fr' : '1fr' }, gap: 3 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Stage breakdown
            </Typography>
            {stages.map((s) => (
              <Box key={s.stage} sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                  <Typography variant="caption">{s.stage}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {s.count}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(s.count / maxStage) * 100}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
            ))}
          </Box>
          {superAdmin && (
          /*
           * Nothing here may set backdrop-filter: it creates a new containing block for
           * position:fixed, which breaks the MUI X Charts tooltip (Popper uses strategy:'fixed'
           * with the container set to the chart's inner div, so viewport coords would resolve
           * relative to this box instead).
           */
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Jobs by recruiter
            </Typography>
            {topWorkload.length === 0 ? (
              <EmptyState compact title="No recruiters assigned yet." />
            ) : (
              /*
               * Horizontal, so the names sit on the band axis and read straight. Vertical bars in
               * a column this narrow force the rotated, clipped labels the Analytics apply-rate
               * chart has to work around with angle: -25.
               */
              <BarChart
                layout="horizontal"
                yAxis={[
                  {
                    data: topWorkload.map((w) => w.name),
                    scaleType: 'band',
                    width: 110,
                    tickLabelStyle: { fontSize: 11 },
                  },
                ]}
                series={[{ data: topWorkload.map((w) => w.jobs), label: 'Jobs', color: barColor }]}
                height={280}
                margin={{ top: 8, right: 16, bottom: 32, left: 8 }}
              />
            )}
          </Box>
          )}
        </Box>
      )}
    </Panel>
  );
};

export default ManagedHiringSection;
